import React, { useState, useEffect, useRef } from 'react';
import { Field } from '../Field/Field.lite';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { CardHeader, Box } from '@mui/material';

// === HOOKS ===
import useOnVisible from 'components/hooks/useOnVisible/useOnVisible';

// === API ===
import { isFocusedBlock, on, onBlock, removeListener, requestBlock } from 'api/prokey/prokey.websocket';

const FieldMemo = React.memo(Field);
function Block({ id, timestamp }) {
  
  //#region [Main] State

  const theme = useTheme();

  const [block, setBlock] = useState({});
  const isVisible = block.is_visible;

  const is_loaded = !!block.Fields;

  const ref_outer = useRef();
  const [ is_in_view ] = useOnVisible( ref_outer );

  //#endregion

  //#region [STYLES]

  const _styles = {
    block_container: {
      display      : (!is_loaded || isVisible) ? '' : 'none',
      borderRadius : '1rem',
      width        : '100%',
      minHeight    : '30px',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      marginBottom: '1rem',
      padding: 0,
    },
    block_container_hidden: {
      display: 'none',
    },
    block_header: {
      padding: '0.5rem',
      color: 'var(--color-primary)',
      borderRadius: '5px',
      fontSize: '120%',
      fontWeight: 'bold',
    },
    block_content_simple: {
      padding    : '0.5rem',
      border     : 0,
      boxShadow  : 0,
      display    : 'inline flex' ,
      flexFlow   : 'row no-wrap' ,
      flexWrap   : 'wrap',
      alignItems : 'flex-start'  ,
      width      : '100%'
    },
    block_content_table: {
      padding    : '0.5rem',
      display    : 'block' ,
      overflowX: 'auto'
    },
    block_grid_wrapper: {
      display: 'grid',
      position: 'static',
      overflowX: 'auto',
      overflowY: 'hidden',
      width: 'max-content',
    },
  };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleBlock(data) {
      //-----------------------------------------------------------------
      setBlock(data)
    }

    onBlock( id, handleBlock );

    return () => {
      removeListener( 'BLOCK-'+id, handleBlock )
    }
  }, [id] );
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function handleBlockFocus(data) {
      //-----------------------------------------------------------------
      if( !is_in_view && ref_outer.current ) {
        ref_outer.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      }
    }

    on( 'BLOCK-FOCUS-'+id , handleBlockFocus );

    return () => {
      removeListener( 'BLOCK-FOCUS-' + id, handleBlockFocus )
    }
  }, [ref_outer,is_in_view] );
  
  useEffect( () => {
    // Focus can arrive before this lazy block mounts. In that case the socket
    // retains the focused id, so load the block even while it is offscreen.
    if(!is_in_view && !isFocusedBlock(id)) return;
    requestBlock( id );
  }, [id, is_in_view, timestamp] );

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.block_container} ref={ref_outer} variant='' >
        {
          !is_loaded
          ?
            is_in_view ? <div>Loading...</div> : null
          :
            <>
              {displayHeader()}
              {block.isTable ? displayTable() : displayFields()}
            </>
        }
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayTable() {
    //-----------------------------------------------------------------
    // sets number of rows dynamically
    const gridTemplateRows = `repeat(1}, 1fr)`;
    // const gridTemplateColumns = `repeat(${block.columns.length}, 1fr)`;
    const gridTemplateColumns = block.columns?.map((col) => {
        let val = `fit-content(${col.length * 11}px)`;
        return val;
      })
      .join(' ');

    const headers = block.columns?.map((col, index) => {

      const TABLE_HEADER = {
        sx    : { paddingBottom: 0 },
        title : col.header,
      }
    
      return <CardHeader key={index} { ...TABLE_HEADER } />;
    })

    return (
      <Box sx={_styles.block_content_table} >
        <Box sx={_styles.block_grid_wrapper} style={{ gridTemplateRows, gridTemplateColumns }} >
          {headers}
          {displayTableRows()}
        </Box>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayTableRows() {
    //-----------------------------------------------------------------
    const block_fields = block.Fields?.map( id => <FieldMemo key={id} id={id} timestamp={timestamp} /> );
  
    return block_fields;
  }

  //-----------------------------------------------------------------
  function displayFields() {
    //-----------------------------------------------------------------
    const block_fields = block.Fields?.map( id => <FieldMemo key={id} id={id} timestamp={timestamp}/> );

    return (
      <Box sx={_styles.block_content_simple} >
        {block_fields}
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayHeader() {
    //-----------------------------------------------------------------
    // Only display the header if there is more than 1 field
    return (
      block.Header !== '' && (
        <CardHeader sx={_styles.block_header} title={block.Header} />
      )
    );
  }

  //#endregion

  return display();
}

export { Block };
